import {
  buildFilteredTaskQueryConfigs,
  buildFilteredTaskQueryKey,
} from '@griply/services';
import { ID, Task, TaskFilterOptions } from '@griply/types';
import { filterTasksClientSide } from '@griply/utils';
import { useQuery } from '@tanstack/react-query';
import { Query } from 'firebase/firestore';
import { useMemo } from 'react';
import { createSubscriptionQuery } from 'shared/lib/@tanstack-query';
import { getFilteredTasks } from 'shared/services/backend-api';

export const queryKey = 'filtered-tasks';

export const useFilteredTasksQuery = (
  uid: ID | undefined,
  filters: TaskFilterOptions | TaskFilterOptions[],
  enabled = true,
) => {
  const filtersArray = useMemo(
    () => (Array.isArray(filters) ? filters : [filters]),
    [filters],
  );

  const queryConfigs = useMemo(
    () => buildFilteredTaskQueryConfigs(filtersArray),
    [filtersArray],
  );

  const filteredTaskQueryKey = useMemo(
    () => buildFilteredTaskQueryKey(filtersArray, queryConfigs),
    [filtersArray, queryConfigs],
  );

  const queryResult = useQuery({
    queryKey: [
      queryKey,
      uid ?? '',
      enabled.toString(),
      ...filteredTaskQueryKey,
    ],
    queryFn: (context) => {
      if (!enabled || !uid) {
        return [];
      }

      if (!queryConfigs.length) {
        return [];
      }

      const allQueries: (() => Promise<Query<Task>>)[] = queryConfigs.map(
        (config) => () =>
          getFilteredTasks(uid, {
            ...config,
            tasksOnly: config.tasksOnly ?? true,
          }),
      );

      return createSubscriptionQuery(allQueries)(context);
    },
    placeholderData: (previous) => previous ?? [],
    enabled: !!uid && enabled,
  });

  const processedData = useMemo(
    () => filterTasksClientSide(queryResult.data ?? [], filtersArray),
    [queryResult.data, filtersArray],
  );

  return {
    ...queryResult,
    data: processedData,
  };
};
